<template>
  <default-layout :breadcrumbs="breadcrumbs">
    <div class="container extra--padding mb-4">
      <page-loading :show="loading || recommendedLoading" />
      <banner />
      <div class="homepage--header-container">
        <coin-summary class="coin--summary-container"></coin-summary>
        <div class="share--catalog-link">
          <share-catalog-link class="mb-4"></share-catalog-link>
          <generate-review-link></generate-review-link>
        </div>
        <membership-summary class="homepage--membership-summary"></membership-summary>
      </div>
      <listings-header />
      <div class="my--order mt-2">
        <v-tabs center-active v-if="categories && categories.length > 0">
          <v-tab
            :href="`#tab-${index}`"
            v-for="(category, index) in categories"
            :key="`category-title-${index}`"
            @click="changeCategory(category)"
          >
            <span>{{ category.name }} </span>
            <span style="margin-left: 4px" v-if="category.id === 3">({{ totalSoldListing }})</span>
            <span style="margin-left: 4px" v-if="category.id === 4"
              >({{ totalRentedListing }})</span
            >
          </v-tab>
          <v-tab-item
            :value="`tab-${index}`"
            v-for="(category, index) in categories"
            :key="`category-content-${index}`"
          >
            <!--            <SectionLoader :show="loading || recommendedLoading" />-->
            <div
              v-for="listing in listings"
              :id="`listing-${listing.uuid}`"
              :key="listing.uuid"
              class="order--item"
            >
              <card-listing :listing="listing" @actionDone="actionDone" />
            </div>
            <pagination
              :push-state="false"
              :meta="listingsMeta.pagination"
              @changePage="changePage"
              :maxVisibleButtons="5"
              v-if="listingsMeta"
            />
          </v-tab-item>
        </v-tabs>
      </div>
    </div>
  </default-layout>
</template>

<script>
import DefaultLayout from '@/layouts/default';
import DefaultHeadMixin from '@/mixins/defaultHead';
import ListingsHeader from '@/components/listing/listings-header';
// import SectionLoader from '@/components/content-loading/section-loading';
import CardListing from '@/components/listing/card-listing';
import CoinSummary from '@/components/utils/coin-summary';
import MembershipSummary from '@/components/utils/membership-summary';
import Pagination from '@/components/utils/fractal-pagination';
import Banner from '@/components/home/banner';
import { mapState } from 'vuex';
import PageLoading from '@/components/content-loading/page-loading.vue';
import ShareCatalogLink from '@/components/utils/share-catalog-link';
import GenerateReviewLink from '@/components/utils/generate-review-link.vue';
import { checkListingDraftOnce } from '@/services/listingDraftRecovery';
import { acquireListingDraftLock, releaseListingDraftLock } from '@/services/listingDraftLock';

export default {
  name: 'index',
  mixins: [DefaultHeadMixin],
  components: {
    GenerateReviewLink,
    PageLoading,
    DefaultLayout,
    ListingsHeader,
    // SectionLoader,
    CardListing,
    Pagination,
    CoinSummary,
    MembershipSummary,
    Banner,
    ShareCatalogLink,
  },
  computed: {
    ...mapState({
      loading: (state) => state.listing.loading,
      categories: (state) => state.listing.categories,
      listings: (state) => state.listing.listings,
      listingsMeta: (state) => state.listing.listingsMeta,
      loggedIn: (state) => state.global.loggedIn,
      recommendedLoading: (state) => state.recommendedListing.loading,
      totalSoldListing: (state) => state.listing.totalSoldListing,
      totalRentedListing: (state) => state.listing.totalRentedListing,
      user: (state) => state.global.user,
    }),
    breadcrumbs() {
      const currentName = this.$t('home.sidebarMenu.myListing');
      return [
        {
          text: this.$t('breadcrumb.homepage'),
          to: '/',
        },
        {
          text: currentName,
          to: this.$router.currentRoute.path,
        },
      ];
    },
  },
  async mounted() {
    this.$store.dispatch('listing/restoreInitialState');
    if (this.$route.query.q) {
      this.$store.commit('listing/SET_SEARCH_QUERY', this.$route.query.q);
    }
    await this.$store.dispatch('masters/getPropertyTypes');
    await this.$store.dispatch('listing/getAllListings', {});
    this.scrollToListingHash();
    await this.$store.dispatch('listing/getTotalSoldListing', this.user.user_number);
    await this.$store.dispatch('listing/getTotalRentedListing', this.user.user_number);

    if (process.client) {
      await this.checkLocalListingDraft();
    }
  },
  methods: {
    async changeCategory(category) {
      this.$store.commit('listing/SET_CATEGORY', category);
      await this.$store.dispatch('listing/getAllListings', {});
    },
    async changePage(page) {
      this.$store.commit('listing/SET_PAGE', page);
      await this.$store.dispatch('listing/getAllListings', {});
    },
    scrollToListingHash() {
      if (!this.$route.hash) {
        return;
      }

      this.$nextTick(() => {
        const target = document.querySelector(this.$route.hash);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    async actionDone() {
      try {
        await Promise.all([
          this.$store.dispatch('coins/getTotalCoins'),
          this.$store.dispatch('listingAvailability/getListingAvailability'),
          this.$store.dispatch('listing/getTotalSoldListing', this.user.user_number),
          this.$store.dispatch('listing/getTotalRentedListing', this.user.user_number),
        ]);
      } catch (e) {
        console.error('Error loading homepage in actionDone: ', e);
      }
    },
    async checkTakeover() {
      if (this.loggedIn) {
        let takeover = null;
        const cookieStr = document.cookie;
        const cookies = require('cookie').parse(cookieStr || '') || {};
        if (cookies.belirumaAgentTakeover) {
          takeover = cookies.belirumaAgentTakeover;
        }

        if (!takeover) {
          let detail = await this.$store.dispatch('takeover/getTakeover');
          if (detail) {
            this.$modal.show('modal--takeover');
          }
        }
      }
    },
    async checkLocalListingDraft() {
      if (!this.user || !this.user.uuid) {
        await this.checkTakeover();
        return;
      }
      const lockAcquired = await acquireListingDraftLock(this.user.uuid);
      if (!lockAcquired) {
        await this.checkTakeover();
        return;
      }
      try {
        const result = await checkListingDraftOnce(this, this.user.uuid);
        if (result.choice === 'continue') {
          await this.$router.push({
            path: '/listing/add',
            query: { draft: result.draft.draftId },
          });
          return;
        }
      } catch (_) {
        await this.$swal('Draft Lokal Tidak Tersedia', 'Pemeriksaan draft lokal gagal.', 'warning');
      }
      releaseListingDraftLock();
      await this.checkTakeover();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/scss/utils/variables';

.homepage--header-container {
  display: flex;
  @media #{$phones} {
    flex-wrap: wrap-reverse;
  }
}

.coin--summary-container {
  width: fit-content;
  margin-bottom: 16px;
}

.share--catalog-link {
  width: 50%;
  margin-bottom: 16px;
  margin-left: 24px;
  @media #{$phones} {
    width: 100%;
    margin-left: 0;
  }
}

.homepage--membership-summary {
  width: 50%;
  margin-bottom: 16px;
  margin-left: 24px;
  @media #{$phones} {
    width: 100%;
    margin-left: 0;
  }
}
</style>
